import React from 'react'

import { Button, Popover, Typography } from '@material-ui/core'
import { DateTime } from 'luxon'

import {
    readyDateMessage,
    transferEndMessage,
    transferStartMessage,
} from './timelineRules'

interface Props {
    anchorEl: HTMLElement | null
    day: DateTime | null
    workStart: DateTime
    workEnd: DateTime
    transferStart: DateTime | null
    transferEnd: DateTime | null
    destReady: DateTime | null
    dtReady: DateTime | null
    minDest: DateTime | null
    minDt: DateTime | null
    isDayOff: boolean
    onClose: () => void
    onSetTransferStart: () => void
    onSetTransferEnd: () => void
    onClearTransfer: () => void
    onSetDest: () => void
    onClearDest: () => void
    onSetDt: () => void
    onClearDt: () => void
    onSetDayOff: () => void
    onClearDayOff: () => void
}

const formatDay = (date: DateTime | null) =>
    date ? date.toFormat('MM/dd/yyyy') : 'Not set'

export const WalkDateMenu = (props: Props) => {
    const {
        anchorEl,
        day,
        workStart,
        workEnd,
        transferStart,
        transferEnd,
        destReady,
        dtReady,
        minDest,
        minDt,
        isDayOff,
        onClose,
    } = props

    const transferStartReason = day
        ? transferStartMessage(day, workStart, workEnd)
        : null
    const transferEndReason = day
        ? transferEndMessage(day, transferStart, workEnd)
        : null
    const destReason = day
        ? readyDateMessage(day, workStart, workEnd, minDest)
        : null
    const dtReason = day
        ? readyDateMessage(
              day,
              workStart,
              workEnd,
              minDt,
              'the destination unit ready date',
          )
        : null

    const run = (action: () => void) => {
        action()
        onClose()
    }

    return (
        <Popover
            open={Boolean(anchorEl && day)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <div style={{ width: 280, padding: 16 }}>
                <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 700, textAlign: 'center' }}
                >
                    {day ? day.toFormat('MMM d, yyyy') : ''}
                </Typography>

                <Section title="Transfer dates">
                    <Typography variant="body2" color="textSecondary">
                        {formatDay(transferStart)} – {formatDay(transferEnd)}
                    </Typography>
                    {!transferStart && transferStartReason && (
                        <Typography variant="caption" color="error">
                            {transferStartReason}
                        </Typography>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            size="small"
                            color="primary"
                            variant="contained"
                            disabled={
                                Boolean(transferStart) ||
                                Boolean(transferStartReason)
                            }
                            onClick={() => run(props.onSetTransferStart)}
                        >
                            Set start
                        </Button>
                        <Button
                            size="small"
                            color="primary"
                            variant="outlined"
                            disabled={
                                !transferStart || Boolean(transferEndReason)
                            }
                            onClick={() => run(props.onSetTransferEnd)}
                        >
                            Set end
                        </Button>
                        <Button
                            size="small"
                            disabled={!transferStart && !transferEnd}
                            onClick={() => run(props.onClearTransfer)}
                        >
                            Clear
                        </Button>
                    </div>
                    {transferEndReason && transferStart && (
                        <Typography variant="caption" color="error">
                            {transferEndReason}
                        </Typography>
                    )}
                </Section>

                <Section title="Destination unit ready">
                    <Typography variant="body2">
                        {formatDay(destReady)}
                    </Typography>
                    {destReason && (
                        <Typography variant="caption" color="error">
                            {destReason}
                        </Typography>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            size="small"
                            color="primary"
                            variant="contained"
                            disabled={Boolean(destReason)}
                            onClick={() => run(props.onSetDest)}
                        >
                            Set
                        </Button>
                        <Button
                            size="small"
                            disabled={!destReady}
                            onClick={() => run(props.onClearDest)}
                        >
                            Clear
                        </Button>
                    </div>
                </Section>

                <Section title="Double transfer unit ready">
                    <Typography variant="body2">
                        {formatDay(dtReady)}
                    </Typography>
                    {dtReason && (
                        <Typography variant="caption" color="error">
                            {dtReason}
                        </Typography>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            size="small"
                            color="primary"
                            variant="contained"
                            disabled={Boolean(dtReason)}
                            onClick={() => run(props.onSetDt)}
                        >
                            Set
                        </Button>
                        <Button
                            size="small"
                            disabled={!dtReady}
                            onClick={() => run(props.onClearDt)}
                        >
                            Clear
                        </Button>
                    </div>
                </Section>

                <Section title="Day off">
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={isDayOff}
                            onClick={() => run(props.onSetDayOff)}
                        >
                            Set day off
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={!isDayOff}
                            onClick={() => run(props.onClearDayOff)}
                        >
                            Clear day off
                        </Button>
                    </div>
                </Section>
            </div>
        </Popover>
    )
}

const Section = (props: { title: string; children: React.ReactNode }) => (
    <div
        style={{
            marginTop: 12,
            padding: 8,
            borderRadius: 8,
            background: '#fafafa',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
        }}
    >
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
            {props.title}
        </Typography>
        {props.children}
    </div>
)
